import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { RefreshControl, TouchableOpacity } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useNavigation } from '@react-navigation/native';

import { CloseIcon } from '@/assets/icons/close';
import ShareIcon from '@/assets/icons/share-icon';
import { ds } from '@/components/lib/design.system';
import { useLaunchIntercomMessenger } from '@/data/intercom/intercom';
import { common, designSystem } from '@/styles/styles';

import { Button } from './ButtonComponent';
import { LineSeparator } from './LineSeparator';
import { DotStatus, ProgressDots } from './ProgressDots';
import { SCREEN_NAV_BAR_HEIGHT } from './ScreenNavBar';
import { SizedBox } from './SizedBox';
import { Text } from './TextComponent';
import { Box, Column, Row, TouchableBox } from './lib/components';
import { LinearGradient } from './linear-gradient/LinearGradient';

const ACTION_TRAY_HEIGHT = 112;

const styles = StyleSheet.create({
    gradient: {
        ...common.zIndexNegative2,
        height: 200,
    },
});

type BetDetailShellProps = {
    title: string;
    progressDots: Array<DotStatus>;
    summary: ReactNode;
    footer: Footer;
    gradientColor: Array<string>;
    shouldShowProgressDots: boolean;
    promo?: ReactNode;
    secondaryButton?: ReactNode;
    hasActionTray?: boolean;
    isRefreshing?: boolean;
    onRefresh?: () => void;
    onSharePress?: () => void;
    children: ReactNode;
};
/**
 * In the future, this component will be used for both pickem and sbk. So it doesn't use useBetDetail Hook directly.
 */
export const BetDetailShell = ({
    title,
    progressDots,
    summary,
    footer,
    gradientColor,
    shouldShowProgressDots,
    promo = null,
    secondaryButton = null,
    hasActionTray = false,
    isRefreshing = false,
    onRefresh,
    onSharePress,
    children,
}: BetDetailShellProps) => {
    const { t } = useTranslation(['common']);
    const { top } = useSafeAreaInsets();
    const { launchIntercomMessenger } = useLaunchIntercomMessenger();

    const { width } = useWindowDimensions();

    return (
        <Box style={{ paddingTop: top }}>
            <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={-1}>
                <LinearGradient
                    colors={gradientColor}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 0, y: 1 }}
                    height={200}
                    width={width}
                    style={[styles.gradient]}
                />
            </Box>
            <ScreenHeader
                shouldShowProgressDots={shouldShowProgressDots}
                title={title}
                dots={progressDots}
                onSharePress={onSharePress}
            />
            <ScrollView
                refreshControl={
                    <RefreshControl
                        enabled
                        colors={[designSystem.colors.neutral700]}
                        tintColor={ds.colorValue.icon.primary}
                        refreshing={isRefreshing}
                        onRefresh={onRefresh}
                    />
                }
            >
                <SizedBox value={24} />
                <Box flex={1} gap="s32" px="s16">
                    <Row>{summary}</Row>
                    {children}
                    <LineSeparator />
                    <Box>
                        <Box gap="s24">
                            {promo ? <PromoFooter promo={promo} /> : null}
                            <Footer rows={footer} />
                        </Box>
                        <SizedBox value={24} />
                        {secondaryButton ? (
                            <>
                                {secondaryButton}
                                <SizedBox value={12} />
                            </>
                        ) : null}
                        <Button label={t('contact_us')} hierarchy={'subtle'} onPress={launchIntercomMessenger} />
                        <SizedBox value={96} />
                        {hasActionTray ? <SizedBox value={ACTION_TRAY_HEIGHT} /> : null}
                    </Box>
                </Box>
            </ScrollView>
        </Box>
    );
};

type ScreenHeader = {
    shouldShowProgressDots: boolean;
    title: string;
    dots: Array<DotStatus>;
    onSharePress?: () => void;
};

export const ScreenHeader = ({ shouldShowProgressDots, title, dots = [], onSharePress }: ScreenHeader) => {
    const navigation = useNavigation();

    const renderTitle = () => (
        <Box flex={1} alignItems="center">
            {shouldShowProgressDots ? (
                <Column gap="s8">
                    <Row justifyContent="center" testID="betCardScreenTitle">
                        <Text variant="titleLarge">{title}</Text>
                    </Row>
                    <Row justifyContent="center">
                        <ProgressDots dots={dots} />
                    </Row>
                </Column>
            ) : (
                <Text variant="titleLarge" testID="betCardScreenTitle">
                    {title}
                </Text>
            )}
        </Box>
    );

    return (
        <Box
            paddingHorizontal="s16"
            marginTop="s10"
            height={SCREEN_NAV_BAR_HEIGHT}
            alignItems="center"
            flexDirection="row"
        >
            <Box flexShrink={1}>
                <TouchableOpacity onPress={() => navigation.goBack()} hitSlop={12}>
                    <CloseIcon />
                </TouchableOpacity>
            </Box>
            {renderTitle()}
            <TouchableOpacity onPress={onSharePress}>
                <ShareIcon />
            </TouchableOpacity>
        </Box>
    );
};

type Footer = Array<{
    label: string;
    value: string;
    testID?: string;
    onLongPress?: () => void;
}>;

const FooterRowContent = ({ row }: { row: Footer[number] }) => (
    <>
        <Text variant="bodySmall" textTransform="capitalize" color="neutral300" testID={row.testID}>
            {row.label}
        </Text>
        <Text variant="bodySmall" color="neutral300" testID={row.testID}>
            {row.value}
        </Text>
    </>
);

const Footer = ({ rows }: { rows: Footer }) => {
    const { t } = useTranslation('common');
    return (
        <Box gap="s8">
            <Text variant="titleSmall" color={ds.colorToken.text.primary}>
                {t('submission_log')}
            </Text>
            <Box>
                {rows.map(row =>
                    row.onLongPress ? (
                        <TouchableBox
                            key={row.label}
                            flexDirection="row"
                            justifyContent="space-between"
                            activeOpacity={0.9}
                            onLongPress={row.onLongPress}
                        >
                            <FooterRowContent row={row} />
                        </TouchableBox>
                    ) : (
                        <Row key={row.label} justifyContent="space-between">
                            <FooterRowContent row={row} />
                        </Row>
                    )
                )}
            </Box>
        </Box>
    );
};

const PromoFooter = ({ promo }: { promo: ReactNode }) => {
    const { t } = useTranslation('common');
    return (
        <Box gap="s8">
            <Text variant="titleSmall" color={ds.colorToken.text.primary}>
                {t('promos')}
            </Text>
            {promo}
        </Box>
    );
};
